import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/Badge";
import { Chip } from "@/components/Chip";
import { Centered, Spinner } from "@/components/Spinner";
import { api } from "@/lib/api";
import { REGISTER_POLL_MS, REGISTER_POLL_TRIES } from "@/lib/config";
import { marketLabel, num } from "@/lib/format";
import { usePublishRefresh } from "@/lib/refresh";
import { colors } from "@/lib/theme";
import type { Market, Ticker } from "@/lib/types";
import { useFetch } from "@/lib/useFetch";

const MARKETS: Market[] = ["KR", "US"];

export function TickersScreen() {
  const state = useFetch((signal) => api.listTickers(undefined, signal), []);

  usePublishRefresh({
    refresh: state.refresh,
    refreshing: state.refreshing,
    updatedAt: state.updatedAt,
  });

  const [symbol, setSymbol] = useState("");
  const [name, setName] = useState("");
  const [market, setMarket] = useState<Market>("KR");
  const [excd, setExcd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // 등록 직후 시세가 채워지기를 기다리는 심볼(서버가 백그라운드로 KIS 를 조회한다).
  const [awaitingQuote, setAwaitingQuote] = useState<string | null>(null);
  const triesRef = useRef(0);

  const submit = async () => {
    if (!symbol.trim() || !name.trim()) {
      setFormError("심볼과 이름을 입력하세요.");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const registered = await api.registerTicker({
        symbol: market === "US" ? symbol.trim().toUpperCase() : symbol.trim(),
        name: name.trim(),
        market,
        excd: market === "US" && excd.trim() ? excd.trim().toUpperCase() : null,
      });
      setSymbol("");
      setName("");
      setExcd("");
      // 서버가 정규화한 심볼로 기다린다(US 대문자화 등).
      triesRef.current = 0;
      setAwaitingQuote(registered.symbol);
      state.refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "등록 실패");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (target: Ticker) => {
    // 브라우저에서는 오클릭이 쉬우므로 삭제 전에 확인한다.
    if (!window.confirm(`${target.symbol} (${target.name}) 을 삭제할까요?`)) return;
    try {
      await api.deleteTicker(target.symbol);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "삭제 실패");
    } finally {
      state.refresh();
    }
  };

  const tickers = state.data?.data ?? [];

  const quoteArrived =
    awaitingQuote !== null &&
    (tickers.find((t) => t.symbol === awaitingQuote)?.rsi ?? null) !== null;

  // 등록 직후 서버 백그라운드 폴링이 끝날 때까지 목록을 재조회한다.
  // updatedAt 이 재조회마다 바뀌므로 이 effect 가 다시 돌면서 다음 시도를 예약한다.
  useEffect(() => {
    if (awaitingQuote === null) return;
    if (quoteArrived || state.error || triesRef.current >= REGISTER_POLL_TRIES) {
      setAwaitingQuote(null);
      return;
    }
    const timer = window.setTimeout(() => {
      triesRef.current += 1;
      state.refresh();
    }, REGISTER_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [awaitingQuote, quoteArrived, state.error, state.updatedAt, state.refresh]);

  return (
    <>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="form-row">
          <input
            className="input"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder={
              market === "KR" ? "종목코드 (예: 005930)" : "심볼 (예: TQQQ)"
            }
            inputMode={market === "KR" ? "numeric" : "text"}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            aria-label="심볼"
          />
          {MARKETS.map((m) => (
            <Chip
              key={m}
              label={marketLabel(m)}
              active={market === m}
              onClick={() => setMarket(m)}
            />
          ))}
        </div>

        <div className="form-row">
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="이름 (예: 삼성전자)"
            autoComplete="off"
            aria-label="이름"
          />
          {market === "US" && (
            <input
              className="input input--excd"
              value={excd}
              onChange={(e) => setExcd(e.target.value)}
              placeholder="EXCD"
              title="거래소 코드(선택). 비우면 서버가 NAS/NYS/AMS 를 차례로 시도해 자동으로 찾는다."
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              aria-label="거래소 코드 (선택)"
            />
          )}
        </div>

        {/* EXCD 는 서버가 폴백으로 자동 탐색하므로 필수가 아니다
            (server/app/kis/universe.py 의 excd_candidates). */}
        {market === "US" && (
          <p className="form-hint">
            EXCD(거래소)는 <strong>비워도 됩니다</strong> — 서버가 자동으로 찾습니다.
            직접 넣으려면 <strong>NAS</strong> 나스닥 · <strong>NYS</strong> 뉴욕 ·{" "}
            <strong>AMS</strong> 아멕스/NYSE Arca(대부분의 레버리지 ETF).
          </p>
        )}

        {formError && <p className="error">{formError}</p>}

        {awaitingQuote && (
          <p className="form-hint">
            <strong>{awaitingQuote}</strong> 시세를 조회하고 있습니다 — 잠시 뒤 RSI 가
            채워집니다.
          </p>
        )}

        <button className="btn" type="submit" disabled={submitting}>
          {submitting ? "등록 중…" : "+ 종목 등록"}
        </button>
      </form>

      {state.loading ? (
        <Centered>
          <Spinner />
        </Centered>
      ) : state.error ? (
        <Centered>
          <p className="error">{state.error}</p>
        </Centered>
      ) : tickers.length === 0 ? (
        <Centered>
          <p className="hint">등록된 종목이 없습니다.</p>
        </Centered>
      ) : (
        <div className="card-list">
          {tickers.map((t) => (
            <TickerRow key={t.symbol} ticker={t} onRemove={remove} />
          ))}
        </div>
      )}
    </>
  );
}

function TickerRow({
  ticker,
  onRemove,
}: {
  ticker: Ticker;
  onRemove: (ticker: Ticker) => void;
}) {
  return (
    <div className={ticker.active ? "card" : "card inactive"}>
      <div className="ticker-row">
        <Link
          className="ticker-main"
          to={`/stock/${encodeURIComponent(ticker.symbol)}`}
        >
          <div className="ticker-head">
            <span className="symbol">{ticker.symbol}</span>
            <Badge label={marketLabel(ticker.market)} color={colors.accent} />
            {!ticker.active && <span className="ticker-meta">(해제됨)</span>}
          </div>
          <div className="ticker-name">{ticker.name}</div>
          <div className="ticker-meta">
            RSI {num(ticker.rsi, 1)}
            {ticker.excd ? ` · ${ticker.excd}` : ""}
          </div>
        </Link>

        <button
          className="del-btn"
          type="button"
          onClick={() => void onRemove(ticker)}
          aria-label={`${ticker.symbol} 삭제`}
        >
          삭제
        </button>
      </div>
    </div>
  );
}
